import React, { useState, useEffect, FormEvent, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Sidebar } from "@/components/Sidebar";
import { PageHeader } from "@/components/Header";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Users,
  UserPlus,
  Mail,
  Phone,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  FolderPlus,
  FolderOpen,
  Loader2,
  Info,
  Download,
} from "lucide-react";

import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
// import Chatbot from "@/components/Chatbot";
// import CallingBot from "@/components/Callingbot";
import BotLauncher from "@/components/BotLauncher";

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  groupId?: number | null;
}

interface Group {
  id: number | null;
  name: string;
}

// ────────────────────────────────────────────────
// Phone formatting helpers (unchanged)
const formatPhoneToDisplay = (phone: string): string | null => {
  if (!phone) return null;
  const digitsOnly = phone.replace(/\D/g, "");
  if (!digitsOnly.match(/\d/)) return null;

  let core = digitsOnly;
  if (core.length === 11 && core.startsWith("1")) core = core.slice(1);

  if (core.length !== 10) return null;

  const area = core.slice(0, 3);
  const prefix = core.slice(3, 6);
  const line = core.slice(6);
  return `(${area}) ${prefix}-${line}`;
};

const isValidPhone = (phone: string): boolean => formatPhoneToDisplay(phone) !== null;

const formatDigitsToDisplay = (digits: string): string => {
  if (!digits) return "";
  const d = digits.slice(0, 10);
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const formatToE164 = (input: string): string | null => {
  if (!input) return null;
  const digits = input.replace(/\D/g, "");
  let core = digits;
  if (core.length === 11 && core.startsWith("1")) core = core.slice(1);
  if (core.length !== 10) return null;
  return `+1${core}`;
};

const e164ToDisplay = (phone: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  let core = digits;
  if (core.length === 11 && core.startsWith("1")) core = core.slice(1);
  if (core.length !== 10) return phone;
  return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6)}`;
};

// ────────────────────────────────────────────────
// CSV parsing (unchanged)
const parseCSV = (text: string) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((line) => {
    const cols = line.split(",").map((c) => c.trim());
    const obj: Record<string, string> = {};
    for (let i = 0; i < headers.length; i++) {
      obj[headers[i]] = cols[i] ?? "";
    }
    return obj;
  });
  return rows;
};

const ContactInfoStep: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Modals
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<string>("");
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Delete confirmations
  const [showDeleteContactConfirm, setShowDeleteContactConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [deleteContactLoading, setDeleteContactLoading] = useState(false);

  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [deleteGroupLoading, setDeleteGroupLoading] = useState(false);

  // CSV import
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{ success: number; failed: number; errors: string[] }>({
    success: 0,
    failed: 0,
    errors: [],
  });

  // CSV help dialog
  const [showCsvHelp, setShowCsvHelp] = useState(false);

  // Forms
  const [groupName, setGroupName] = useState("");
  const [contactForm, setContactForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [mobileMenuToggle, setMobileMenuToggle] = useState<(() => void) | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // ────────────────────────────────────────────────
  // Fetch data
  const fetchContacts = async () => {
    try {
      setLoading(true);

      let grpMapped: Group[] = [];
      try {
        const grpRes = await axiosInstance.get(endpoints.contact_groups);
        const grpData = Array.isArray(grpRes.data) ? grpRes.data : [];
        grpMapped = grpData.map((g: any) => ({
          id: g.id ?? null,
          name: g.name,
        }));
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }

      const response = await axiosInstance.get(endpoints.agent_user);
      const contactsData = Array.isArray(response.data) ? response.data : [];

      const mapped: Contact[] = contactsData.map((item: any) => {
        const groupId = item.group ?? item.group_id ?? item.contact_group ?? item.groupId ?? null;
        let companyName = item.company?.trim() || "Uncategorized";

        if (groupId) {
          const found = grpMapped.find((g) => g.id === groupId);
          if (found) companyName = found.name;
        }

        return {
          id: item.id.toString(),
          firstName: item.first_name || "",
          lastName: item.last_name || "",
          email: item.email || "",
          phone: item.phone ? e164ToDisplay(item.phone) : "",
          company: companyName,
          groupId: groupId,
        };
      });

      setContacts(mapped);

      const companiesFromContacts = [...new Set(mapped.map((c) => c.company).filter(Boolean))];
      companiesFromContacts.forEach((name) => {
        if (!grpMapped.find((x) => x.name === name)) {
          grpMapped.push({ id: null, name });
        }
      });

      grpMapped.sort((a, b) => a.name.localeCompare(b.name));
      setGroups(grpMapped);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const meRes = await axiosInstance.get(endpoints.me);
        const userData = meRes.data || {};
        const isTrialStarter =
          (userData.plan_name === "Starter" || userData.plan_name === "Starter Annual") &&
          Boolean(userData.is_in_free_trial || userData.is_in_active_trial);

        if (!["Pro", "Pro+", "Pro Annual", "Pro+ Annual"].includes(userData.plan_name) && !isTrialStarter) {
          toast({
            title: "Upgrade Required",
            description: "This feature is only available on Pro or Pro+ plans unless you are on a Starter free trial.",
            variant: "destructive",
          });
          navigate("/dashboard", { replace: true });
          return;
        }
      } catch (err) {
        console.error("Failed to verify user:", err);
      }

      fetchContacts();
    };

    init();
  }, [navigate, toast]);

  // ────────────────────────────────────────────────
  // Group CRUD
  const openGroupModal = () => {
    setGroupName("");
    setIsGroupModalOpen(true);
  };

  const handleCreateGroup = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = groupName.trim();

    if (!trimmed) {
      toast({ title: "Error", description: "Please enter a group name", variant: "destructive" });
      return;
    }

    if (groups.some((g) => g.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: "Error", description: "This group already exists", variant: "destructive" });
      return;
    }

    try {
      const res = await axiosInstance.post(endpoints.contact_groups, { name: trimmed });
      const created = res.data;
      const newGroup: Group = { id: created.id ?? null, name: created.name || trimmed };

      setGroups((prev) => {
        const next = [...prev, newGroup].sort((a, b) => a.name.localeCompare(b.name));
        return next;
      });

      setIsGroupModalOpen(false);
      setGroupName("");
      setExpandedGroups((prev) => new Set([...prev, newGroup.name]));
      setCurrentGroup(newGroup.name);

      toast({ title: "Success", description: `Group "${newGroup.name}" created` });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.response?.data?.name?.[0] || "Failed to create group",
        variant: "destructive",
      });
    }
  };

  // ────────────────────────────────────────────────
  // Contact modal
  const openContactModal = (group: string, contact?: Contact) => {
    setCurrentGroup(group);
    setErrors({});

    if (contact) {
      setEditingContact(contact);
      setContactForm({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
      });
    } else {
      setEditingContact(null);
      setContactForm({ firstName: "", lastName: "", email: "", phone: "" });
    }

    setIsContactModalOpen(true);
  };

  // ────────────────────────────────────────────────
  // CSV Import
  const handleImportButton = (groupName?: string) => {
    if (groupName) setCurrentGroup(groupName);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportSummary({ success: 0, failed: 0, errors: [] });

    try {
      const text = await file.text();
      const records = parseCSV(text);

      if (records.length === 0) {
        toast({ title: "No data", description: "CSV appears empty", variant: "destructive" });
        return;
      }

      let success = 0;
      let failed = 0;
      const errorsList: string[] = [];

      for (const [idx, r] of records.entries()) {
        const firstName = r["first_name"] ?? r["firstname"] ?? r["first name"] ?? r["first"] ?? "";
        const lastName = r["last_name"] ?? r["lastname"] ?? r["last name"] ?? r["last"] ?? "";
        const email = r["email"] ?? r["emailaddress"] ?? r["mail"] ?? "";
        const phoneRaw = r["phone"] ?? r["phonenumber"] ?? r["phone number"] ?? r["mobile"] ?? "";
        const csvCompany = r["company"] ?? r["companyname"] ?? r["organization"] ?? r["org"] ?? "";

        const targetCompany = currentGroup || csvCompany || "Uncategorized";
        const foundGroup = groups.find((g) => g.name === currentGroup);

        if (!firstName || !lastName || !email) {
          failed++;
          errorsList.push(`Row ${idx + 2}: missing required fields (first_name, last_name, email)`);
          continue;
        }

        const e164 = formatToE164(phoneRaw) || null;

        const payload: any = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: String(email).trim().toLowerCase(),
          phone: e164,
          company: targetCompany,
        };

        if (foundGroup?.id) {
          payload.group = foundGroup.id;
        }

        try {
          await axiosInstance.post(endpoints.agent_user, payload);
          success++;
        } catch (err: any) {
          failed++;
          const msg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
          errorsList.push(`Row ${idx + 2}: ${msg}`);
        }
      }

      setImportSummary({ success, failed, errors: errorsList });
      await fetchContacts();

      toast({
        title: "Import Complete",
        description: `${success} imported, ${failed} failed`,
      });
    } catch (err: any) {
      toast({ title: "Import Error", description: err.message || "Failed to process file", variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ────────────────────────────────────────────────
  // Sample CSV download
  const downloadSampleCSV = () => {
    const content = [
      "first_name,last_name,email,phone,company",
      `John,Doe,john.doe@example.com,"(555) 123-4567",Clients`,
      `Sarah,Smith,sarah@startup.io,202-555-0198,`,
      `Mike,Johnson,mike.j@techcorp.com,4155559876,Partners`,
    ].join("\n");

    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "launchiq-contacts-sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ────────────────────────────────────────────────
  // Save / Update Contact
  const handleSaveContact = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!contactForm.firstName.trim()) newErrors.firstName = "First name is required";
    if (!contactForm.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!contactForm.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!contactForm.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhone(contactForm.phone)) {
      newErrors.phone = "Invalid phone number. Example: (123) 456-7890";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);

    try {
      const e164Phone = formatToE164(contactForm.phone);
      if (!e164Phone) throw new Error("Invalid phone format");

      const foundGroup = groups.find((g) => g.name === currentGroup);

      const payload: any = {
        first_name: contactForm.firstName.trim(),
        last_name: contactForm.lastName.trim(),
        email: contactForm.email.trim().toLowerCase(),
        phone: e164Phone,
        company: currentGroup || "Uncategorized",
      };

      if (foundGroup?.id) {
        payload.group = foundGroup.id;
      }

      let response;
      if (editingContact) {
        response = await axiosInstance.put(`${endpoints.agent_user}${editingContact.id}/`, payload);
      } else {
        response = await axiosInstance.post(endpoints.agent_user, payload);
      }

      await fetchContacts();
      setIsContactModalOpen(false);
      setContactForm({ firstName: "", lastName: "", email: "", phone: "" });
      setEditingContact(null);
      setErrors({});

      toast({
        title: "Success",
        description: editingContact
          ? `Contact "${contactForm.firstName} ${contactForm.lastName}" updated`
          : `Contact "${contactForm.firstName} ${contactForm.lastName}" created`,
      });
    } catch (err: any) {
      const errorData = err.response?.data;
      const fieldErrors: Record<string, string> = {};

      if (errorData && typeof errorData === "object") {
        const fieldMapping: Record<string, string> = {
          first_name: "firstName",
          last_name: "lastName",
          email: "email",
          phone: "phone",
          company: "company",
          group: "group",
        };

        Object.keys(errorData).forEach((key) => {
          const frontendKey = fieldMapping[key] || key;
          const msg = Array.isArray(errorData[key]) ? errorData[key][0] : errorData[key];
          fieldErrors[frontendKey] = typeof msg === "string" ? msg : JSON.stringify(msg);
        });

        setErrors(fieldErrors);

        const firstErr = Object.values(fieldErrors)[0];
        if (firstErr) {
          toast({ title: "Validation Error", description: firstErr, variant: "destructive" });
        }
      } else {
        toast({
          title: "Failed to save",
          description: err.response?.data?.detail || err.message || "Unexpected error",
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ────────────────────────────────────────────────
  // Delete handlers
  const handleDeleteContact = async (id: string) => {
    try {
      await axiosInstance.delete(`${endpoints.agent_user}${id}/`);
      await fetchContacts();
      toast({ title: "Success", description: "Contact deleted" });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to delete contact",
        variant: "destructive",
      });
    }
  };

  const handleDeleteGroup = async (groupNameToDelete: string) => {
    const grp = groups.find((g) => g.name === groupNameToDelete);
    if (!grp) return;

    try {
      if (grp.id) {
        await axiosInstance.delete(`${endpoints.contact_groups}${grp.id}/`);
      }
      setGroups((prev) => prev.filter((g) => g.name !== groupNameToDelete));
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.delete(groupNameToDelete);
        return next;
      });
      await fetchContacts();
      toast({ title: "Success", description: `Group "${groupNameToDelete}" deleted` });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to delete group",
        variant: "destructive",
      });
    }
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;
    setDeleteContactLoading(true);
    try {
      await handleDeleteContact(contactToDelete.id);
      setShowDeleteContactConfirm(false);
      setContactToDelete(null);
    } finally {
      setDeleteContactLoading(false);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    setDeleteGroupLoading(true);
    try {
      await handleDeleteGroup(groupToDelete);
      setShowDeleteGroupConfirm(false);
      setGroupToDelete(null);
    } finally {
      setDeleteGroupLoading(false);
    }
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // ────────────────────────────────────────────────
  // Stats & grouping
  const totalContacts = contacts.length;
  const totalGroups = groups.length;

  const groupedContacts = contacts.reduce((acc, contact) => {
    const group = contact.company || "Uncategorized";
    if (!acc[group]) acc[group] = [];
    acc[group].push(contact);
    return acc;
  }, {} as Record<string, Contact[]>);
  const handleRegisterMobileToggle = useCallback((fn: () => void) => {
    setMobileMenuToggle(() => fn);
  }, []);
  // ────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar registerMobileToggle={handleRegisterMobileToggle} />
      <main className="flex-1 flex flex-col">
        <PageHeader
          // title="My Hub"
          // description="Create your marketing launch in simple steps"
          mobileMenuOffset={true}
          onMenuClick={mobileMenuToggle ?? undefined}
        />

        <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full space-y-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelected}
            className="hidden"
          />

          {/* Header */}
          <div className="space-y-3">
            <h1 className="text-3xl font-bold text-gray-900">My Hub</h1>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">How launches work in LaunchIQ</p>
              <ul className="list-disc pl-5 space-y-1 text-blue-700">
                <li>
                  Contacts must be organized into <strong>groups</strong> to be used in launches
                </li>
                <li>You can only send launches to entire <strong>groups</strong>, not individuals</li>
              </ul>
              <p className="mt-2 text-blue-700">
                Create groups and add contacts to start building your audience.
              </p>
            </div>

            <p className="text-gray-600">Manage your contacts and groups for LaunchIQ launches</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Contacts</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{totalContacts}</p>
                </div>
                <Users className="h-10 w-10 text-blue-600 opacity-80" />
              </div>
            </Card>
            <Card className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Groups</p>
                  <p className="text-2xl sm:text-4xl font-bold mt-1 sm:mt-2">{totalGroups}</p>
                </div>
                <FolderOpen className="h-10 w-10 text-purple-600 opacity-80" />
              </div>
            </Card>
          </div>

          {/* Groups Section */}
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Contact Groups</h2>
                <p className="text-gray-600">Click on a group to view contacts</p>
              </div>
              <Button onClick={openGroupModal} size="lg">
                <FolderPlus className="mr-2 h-5 w-5" />
                Add Group
              </Button>
            </div>

            {loading ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <Loader2 className="h-10 w-10 animate-spin mx-auto text-blue-600" />
                  <p className="mt-4 text-gray-600">Loading...</p>
                </CardContent>
              </Card>
            ) : groups.length === 0 ? (
              <Card>
                <CardContent className="py-20 text-center text-gray-600">
                  <FolderOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No groups yet</p>
                  <p className="mt-2">Click "Add Group" to create your first one</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin">
                {/* Scrollable container for many groups + contacts */}
                {groups.map((group) => {
                  const groupContacts = groupedContacts[group.name] || [];
                  const isExpanded = expandedGroups.has(group.name);

                  return (
                    <Card key={group.name} className="overflow-hidden">
                      <button
                        onClick={() => toggleGroup(group.name)}
                        className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-4">
                          <FolderOpen className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="text-lg font-semibold">{group.name}</h3>
                            <p className="text-sm text-gray-600">
                              {groupContacts.length} contact{groupContacts.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>

                      {isExpanded && (
                        <div className="p-3 sm:p-6 bg-white space-y-4 sm:space-y-6">
                          <div className="flex flex-col sm:flex-row sm:justify-end items-stretch sm:items-center gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={() => handleImportButton(group.name)}
                                    size="sm"
                                    variant="outline"
                                    disabled={importing}
                                    className="gap-1.5 w-full sm:w-auto justify-center"
                                  >
                                    <FolderPlus className="h-4 w-4" />
                                    Import CSV
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  Import multiple contacts at once.<br />
                                  Required: first_name, last_name, email<br />
                                  Optional: phone, company
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowCsvHelp(true)}
                              className="text-muted-foreground hover:text-foreground gap-1 w-full sm:w-auto justify-center"
                            >
                              <Info className="h-4 w-4" />
                              CSV Format
                            </Button>

                            <Button onClick={() => openContactModal(group.name)} size="sm" className="w-full sm:w-auto justify-center">
                              <UserPlus className="mr-2 h-4 w-4" />
                              Add Contact
                            </Button>
                          </div>

                          <div className="max-h-96 overflow-y-auto border rounded-md">
                            <Table>
                              <TableHeader className="sticky top-0 bg-white z-10">
                                <TableRow>
                                  <TableHead>Name</TableHead>
                                  <TableHead className="hidden sm:table-cell">Email</TableHead>
                                  <TableHead className="hidden sm:table-cell">Phone</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {groupContacts.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                                      No contacts yet. Add some using the buttons above.
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  groupContacts.map((contact) => (
                                    <TableRow key={contact.id} className="hover:bg-gray-50">
                                      <TableCell className="font-medium">
                                        {contact.firstName} {contact.lastName}
                                      </TableCell>
                                      <TableCell className="hidden sm:table-cell max-w-xs truncate">{contact.email}</TableCell>
                                      <TableCell className="hidden sm:table-cell">{contact.phone || "—"}</TableCell>
                                      <TableCell className="text-right space-x-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => openContactModal(group.name, contact)}
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            setContactToDelete(contact);
                                            setShowDeleteContactConfirm(true);
                                          }}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                )}
                              </TableBody>
                            </Table>
                          </div>

                          {groupContacts.length === 0 && (
                            <div className="flex justify-end pt-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setGroupToDelete(group.name);
                                  setShowDeleteGroupConfirm(true);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Group
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ──────────────────────────────────────────────── */}
        {/* Dialogs */}

        {/* Add Group */}
        <Dialog open={isGroupModalOpen} onOpenChange={setIsGroupModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl">Create New Group</DialogTitle>
              <DialogDescription>Create a new group to organize your contacts.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateGroup}>
              <div className="py-4">
                <Label htmlFor="groupName">Group Name</Label>
                <Input
                  id="groupName"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Marketing Team, VIP Clients, ..."
                  className="mt-2"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setIsGroupModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Group</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* CSV Help / Sample */}
        <Dialog open={showCsvHelp} onOpenChange={setShowCsvHelp}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] p-4 overflow-hidden">
            <DialogHeader>
              <DialogTitle>Import Contacts via CSV</DialogTitle>
              <DialogDescription>
                Upload a .csv file with at least the required columns below.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[65vh] overflow-y-auto px-6 py-4 space-y-6">
              <div className="bg-muted/60 p-4 rounded border">
                <h4 className="font-medium mb-2">Required columns</h4>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li><code>first_name</code></li>
                  <li><code>last_name</code></li>
                  <li><code>email</code></li>
                  <li><code>phone</code> (e.g. (555) 123-4567 or 5551234567)</li>
                </ul>

                <h4 className="font-medium mt-5 mb-2">Optional columns</h4>
                <ul className="list-disc pl-5 text-sm space-y-1">
                 
                  <li><code>company</code> — ignored if importing to a specific group</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-3">Example CSV:</h4>
                <div className="bg-gray-900 text-gray-200 font-mono text-sm p-4 rounded-md overflow-x-auto">
                  <pre className="whitespace-pre">
                    {`first_name,last_name,email,phone,company
John,Doe,john.doe@example.com,"(555) 123-4567",Clients
Sarah,Smith,sarah@startup.io,2025550198,
Mike,Johnson,mike.j@techcorp.com,415-555-9876,Partners`}
                  </pre>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={downloadSampleCSV} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Download Sample CSV
                </Button>
              </div>
            </div>

            <DialogFooter className="p-4">
              <Button variant="outline" onClick={() => setShowCsvHelp(false)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowCsvHelp(false);
                  handleImportButton(currentGroup);
                }}
              >
                Upload CSV Now
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Contact Confirm */}
        <AlertDialog open={showDeleteContactConfirm} onOpenChange={setShowDeleteContactConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete contact?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove{" "}
                {contactToDelete ? `${contactToDelete.firstName} ${contactToDelete.lastName}` : "this contact"}.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteContactLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteContact}
                disabled={deleteContactLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteContactLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Group Confirm */}
        <AlertDialog open={showDeleteGroupConfirm} onOpenChange={setShowDeleteGroupConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">Delete group?</AlertDialogTitle>
              <AlertDialogDescription>
                Delete group "{groupToDelete}"? This removes the group but **does not delete the contacts**.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteGroupLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteGroup}
                disabled={deleteGroupLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteGroupLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Group
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add/Edit Contact Modal */}
        <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-[680px] max-h-[90vh] overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b bg-background px-4 py-4 sm:px-6">
              <DialogTitle className="break-words text-xl leading-tight sm:text-2xl">
                {editingContact ? "Edit Contact" : "Add Contact"} — {currentGroup}
              </DialogTitle>
              <DialogDescription className="text-sm sm:text-base">
                {editingContact ? "Update contact details." : "Enter new contact information."}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveContact} className="flex max-h-[calc(90vh-88px)] flex-col">
              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
                <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={contactForm.firstName}
                    onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                    placeholder="John"
                    className={errors.firstName ? "border-red-500" : ""}
                  />
                  {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={contactForm.lastName}
                    onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                    placeholder="Doe"
                    className={errors.lastName ? "border-red-500" : ""}
                  />
                  {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    className={`pl-10 ${errors.phone ? "border-red-500" : ""}`}
                    value={contactForm.phone}
                    onChange={(e) => {
                      let digits = e.target.value.replace(/\D/g, "");
                      if (digits.startsWith("1") && digits.length > 10) digits = digits.slice(1);
                      digits = digits.slice(0, 10);
                      setContactForm({ ...contactForm, phone: formatDigitsToDisplay(digits) });
                    }}
                    placeholder="(123) 456-7890"
                  />
                </div>
                {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
              </div>

              {errors.general && (
                <div className="rounded border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-600">{errors.general}</p>
                </div>
              )}
                </div>
              </div>

              <DialogFooter className="shrink-0 border-t bg-background px-4 py-4 sm:px-6">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setIsContactModalOpen(false);
                    setErrors({});
                  }}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="w-full sm:w-auto">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingContact ? (
                    "Update Contact"
                  ) : (
                    "Add Contact"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        {/* <CallingBot />
        <Chatbot /> */}
        <BotLauncher />
      </main>
    </div>
  );
};

export default ContactInfoStep;
